import React, {
  useCallback, useEffect, useRef, useState
} from 'react';

import { Checkbox, FormControl } from '@primer/react';
import { PersonAddIcon } from '@primer/octicons-react';

import AutoSizer from 'react-virtualized-auto-sizer';
import { VariableSizeList } from 'react-window';

import isFunction from 'lodash/isFunction';
import keyBy from 'lodash/keyBy';
import some from 'lodash/some';
import sortBy from 'lodash/sortBy';

import { getThemeColor } from '../../../utils/theme-utils';
import { getUserDisplayName } from '../../../utils/name_utils';

import Avatar from '../../common/Avatar/Avatar';
import Button from '../../common/Button/Button';
import CardHeader from '../../common/Labels/CardHeader';
import CardView from '../../common/CardView/CardView';
import Icon from '../../common/icons/Icon';
import NoUsersSlate from '../../slates/NoUserSlate';
import SearchInput from '../../common/TextInputs/SearchInput/SearchInput';
import VerticalGradientOverlay from '../../common/overlays/VerticalGradientOverlay';

const filterCollaboratorsBySearchValue = (searchStr = '', users = []) => users.filter((user) => {
  const displayName = user?.email || user?.username;

  if (!searchStr?.length) {
    return true;
  }
  return displayName && displayName?.toLowerCase().includes(searchStr.toLowerCase());
})

const sortCollaboratorsAlphabetically = (users = []) => sortBy(users, user => user?.email || user?.username)

const UserSelectionItem = ({
  user,
  onChange,
  disabled = false,
  selected = false,
}) => {
  const displayName = getUserDisplayName(user)
  const selectionId = user?.email || user?.username;
  return (
    <div
      id={`user-selection-item-${selectionId}`}
      className="p-1 m-1 flex justify-between items-center w-full"
    >
      <FormControl disabled={disabled}>
        <FormControl.LeadingVisual>
          <Avatar />
        </FormControl.LeadingVisual>
        <Checkbox
          id={`selection-checkbox-${user.id}`}
          checked={selected}
          onChange={() => onChange(user.id)}
        />
        <FormControl.Label>{displayName}</FormControl.Label>
      </FormControl>
    </div>
  )
}

const ListRow = ({ 
  index, 
  style,
  data
}) => {
  const {
    users,
    listRef,
    rowHeights,
    isUserInWorkspace,
    isUserSelected,
    onSelectionChanged
  } = data;
  const rowRef = useRef({});
  const user = users?.length ? users[index] : null;
  const userID = user?.id;

  const setRowHeight = useCallback((rowIndex, size) => {
    listRef?.current?.resetAfterIndex(0);
    rowHeights.current = { ...rowHeights.current, [rowIndex]: size }
  }, [listRef, rowHeights]);

  useEffect(() => {
    if (rowRef.current) {
      setRowHeight(index, rowRef.current.clientHeight);
    }
  }, [index, setRowHeight]);

  return (
    <div style={style}>
      <UserSelectionItem
        key={userID}
        user={user}
        disabled={isUserInWorkspace(userID)}
        selected={isUserSelected(userID)}
        onChange={onSelectionChanged}
      />
    </div>
  )
}

const VirtualizedListSection = ({ 
  width,
  users,
  listRef,
  rowHeights,
  isUserInWorkspace,
  isUserSelected,
  onSelectionChanged,
  height,
  userItemSize,
  getRowHeight
}) => {
  if (!users?.length) {
    return null;
  }

  return (
    <VariableSizeList
      ref={listRef}
      height={height}
      itemCount={users?.length || 0}
      estimatedItemSize={userItemSize}
      itemSize={getRowHeight}
      width={width}
      itemData={{
        users,
        listRef,
        rowHeights,
        isUserInWorkspace,
        isUserSelected,
        onSelectionChanged
      }}
    >
      {ListRow}
    </VariableSizeList>
  )
}

const UserSearchPanel = ({
  users,
  isUserSelected,
  isUserInWorkspace,
  onSelectionChanged,
  height = 200,
  userItemSize = 40,
  disableScrollFading = false,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const filteredUsers = sortCollaboratorsAlphabetically(filterCollaboratorsBySearchValue(searchQuery, users));

  const listRef = useRef({});
  const rowHeights = useRef({});

  const getRowHeight = useCallback((index) => {
    const rowHeight = rowHeights?.current[index]
    if (rowHeight && rowHeight < userItemSize) {
      return userItemSize;
    }
    return rowHeight || userItemSize;
  }, [userItemSize, rowHeights]);
  
  return (
    <div className="relative">
      <div className="w-full flex flex-grow-2 justify-start items-center h-full">
        {
          users?.length > 4 && (
            <SearchInput
              className="h-10 relative flex-grow border border-border-dark rounded mt-2 pl-10 mr-2 bg-white"
              ariaLabel="Search by email"
              placeholder="Search by email"
              value={searchQuery}
              onChange={event => setSearchQuery(event.target.value)}
            />
          )
        }
      </div>
      <div className="overflow-scroll py-4 flex-grow">
        {
          !disableScrollFading && (
            <VerticalGradientOverlay className="top-0 left-0 right-0" fromDirection="b" />
          )
        }
        <AutoSizer disableHeight>
          {({ width }) => (
            <VirtualizedListSection
              width={width}
              users={filteredUsers}
              listRef={listRef}
              rowHeights={rowHeights}
              isUserInWorkspace={isUserInWorkspace}
              isUserSelected={isUserSelected}
              onSelectionChanged={onSelectionChanged}
              height={height}
              userItemSize={userItemSize}
              getRowHeight={getRowHeight}
            />
          )}
        </AutoSizer>
        {
          !disableScrollFading && (
            <VerticalGradientOverlay className="bottom-0 left-0 right-0" fromDirection="t" />
          )
        }
      </div>
    </div>
  )
}

export const WorkspaceInviteForm = ({
  onFormSubmit,
  users = [],
  workspaceUsers = [],
  userItemSize = 40,
  height = 200,
}) => {
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [userDataMap, setUserDataMap] = useState({});

  const isUserSelected = useCallback(userId => selectedUserIds.includes(userId), [selectedUserIds])
  const isUserInWorkspace = userId => some(workspaceUsers, user => user.id === userId);

  useEffect(() => {
    if (users?.length) {
      setUserDataMap(keyBy(users, user => user.id))
    } else {
      setUserDataMap({})
    }
  }, [users])

  const onSelectionChanged = useCallback((userId) => {
    if (isUserSelected(userId)) {
      setSelectedUserIds(selectedUserIds.filter(id => id !== userId))
    } else {
      setSelectedUserIds([...selectedUserIds, userId])
    }
  }, [isUserSelected, selectedUserIds])

  const onFormSubmission = useCallback(() => {
    if (isFunction(onFormSubmit) && selectedUserIds?.length) {
      const submission = selectedUserIds.map(userId => ({
        userId,
        ...userDataMap[userId],
      }))
      onFormSubmit(submission);
    }
  }, [onFormSubmit, selectedUserIds, userDataMap]);

  return (
    <CardView className="flex flex-col justify-between">
      <div className="flex flex-col">
        <div className="mb-2 flex items-center">
          <Icon fill={getThemeColor('brand')} icon={PersonAddIcon} size={24} />
          <CardHeader className="ml-2" bold>Invite to Workspace</CardHeader>
        </div>
        {
          users?.length ? (
            <UserSearchPanel
              users={users}
              isUserInWorkspace={isUserInWorkspace}
              isUserSelected={isUserSelected}
              onSelectionChanged={onSelectionChanged}
              height={height}
              userItemSize={userItemSize}
              disableScrollFading
            />
          ) : null
        }
      </div>
      {
        !users?.length ? (
          (
            <NoUsersSlate
              mainText="We couldn't find any users in your product space."
            />
          )
        ) : null
      }
      <Button
        className="mt-4 w-full"
        variant="primary"
        disabled={selectedUserIds?.length < 1}
        onClick={onFormSubmission}
      >
        Send Invitations
      </Button>
    </CardView>
  )
}

export default WorkspaceInviteForm;
